import { useEffect, useState } from "react";
import { toast } from "sonner";

import ConfirmMessageModal from "components/ConfirmMessageModal";
import { renderWhatsAppFormatting } from "lib/whatsappFormatting";
import radioCheck from "assets/svg/radio-check.svg";
import { formatTmNoForDisplay } from "utils/tmNumberDisplay";
import {
  OppositionWorkflowStep,
  getOppositionFileEvidenceAffidavit,
  getOppositionProgress,
  previewOppositionInformClientAboutFilingEvidence,
  sendOppositionInformClientAboutFilingEvidence,
  type OppositionFileEvidenceAffidavitItem,
  type OppositionProgressStep,
} from "api/opposition";

export const OPPOSITION_INFORM_CLIENT_ABOUT_FILING_EVIDENCE_FORM_ID =
  "opposition-inform-client-about-filing-evidence-form";

function RadioCircle({ active }: { active: boolean }) {
  if (active) return <img src={radioCheck} alt="" width={14} height={14} />;
  return (
    <span
      aria-hidden
      className="inline-block w-[14px] h-[14px] rounded-full border border-[#C3C3C3] bg-white"
    />
  );
}

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSentStatusChange?: (sent: boolean) => void;
};

export default function OppositionInformClientAboutFilingOfEvidenceView({
  leadId,
  onSuccess,
  onSentStatusChange,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [marks, setMarks] = useState<OppositionFileEvidenceAffidavitItem[]>([]);
  const [previewMessage, setPreviewMessage] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [hasSent, setHasSent] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"send" | "resend">("send");

  useEffect(() => {
    onSentStatusChange?.(hasSent);
  }, [hasSent, onSentStatusChange]);

  useEffect(() => {
    if (!leadId) {
      setMarks([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getOppositionFileEvidenceAffidavit(leadId),
      getOppositionProgress(leadId).catch(() => null),
    ])
      .then(([data, progress]) => {
        if (cancelled) return;
        // Only marks with both 21zzb saved AND 21zzc available are eligible
        // (the backend requires 21zzc on the included ids).
        setMarks(
          data.conflictingMarks.filter(
            (m) =>
              m.dateOfFilingEvidenceAffidavit != null &&
              m.lastDateForEvidenceByConflictingProprietor != null
          )
        );
        if (progress) {
          const step = progress.steps.find(
            (s: OppositionProgressStep) =>
              s.slug ===
              OppositionWorkflowStep.OP_S3_16_InformClientAboutFilingOfEvidenceByOpponent
          );
          setHasSent(Boolean(step?.isCompleted));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load step data"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const selectedIds = marks.map((m) => m.id);

  useEffect(() => {
    if (!leadId || selectedIds.length === 0) {
      setPreviewMessage(null);
      setPhoneNumber("");
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    previewOppositionInformClientAboutFilingEvidence(leadId, {
      conflictingMarkIds: selectedIds,
    })
      .then((res) => {
        if (cancelled) return;
        setPreviewMessage(res.message);
        setPhoneNumber(res.recipientPhone);
      })
      .catch((err) => {
        if (cancelled) return;
        setPreviewError(
          err instanceof Error ? err.message : "Failed to load preview"
        );
        setPreviewMessage(null);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, selectedIds.join("|")]);

  const canSend = !previewError && !!previewMessage && selectedIds.length > 0;

  const triggerSend = (mode: "send" | "resend") => {
    if (!leadId || sending) return;
    if (!canSend) {
      toast.error(
        previewError ??
          "Save Date of Filing Evidence Affidavit for at least one application first."
      );
      return;
    }
    setConfirmMode(mode);
    setShowConfirm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || sending) return;
    if (hasSent) {
      onSuccess?.(leadId);
      return;
    }
    triggerSend("send");
  };

  const handleConfirmProceed = () => {
    if (!leadId) return;
    setShowConfirm(false);
    setSending(true);
    (async () => {
      try {
        await sendOppositionInformClientAboutFilingEvidence(leadId, {
          conflictingMarkIds: selectedIds,
        });
        setHasSent(true);
        toast.success(
          confirmMode === "resend"
            ? "Message resent to client successfully."
            : "Message sent to client successfully."
        );
        onSuccess?.(leadId);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to send");
      } finally {
        setSending(false);
      }
    })();
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  return (
    <form
      id={OPPOSITION_INFORM_CLIENT_ABOUT_FILING_EVIDENCE_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-[22px] w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {marks.length > 1 && (
        <div className="flex flex-row flex-wrap gap-3">
          {marks.map((m) => (
            <div
              key={m.id}
              className="flex items-center gap-2 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[140px] bg-[#222222] text-white"
              style={{ fontSize: "0.75rem" }}
            >
              <RadioCircle active />
              {formatTmNoForDisplay(m.applicationNo ?? "")}
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-5">
        <p className="text-[16px] leading-5 text-[#222]">Message Preview</p>
        <div className="rounded-[6px] border border-[#EEEEEE] bg-white p-[14px] min-h-[148px]">
          {previewLoading && !previewMessage ? (
            <p className="font-sans text-[12px] leading-5 text-[#6D6D6D] m-0">
              Loading preview…
            </p>
          ) : previewMessage ? (
            <pre className="font-sans text-[12px] leading-5 text-[#222] m-0 whitespace-pre-wrap break-words">
              {renderWhatsAppFormatting(previewMessage)}
            </pre>
          ) : (
            <p className="font-sans text-[12px] leading-5 text-[#6D6D6D] m-0">
              {previewError ??
                "Save Date of Filing Evidence Affidavit in Step 15 before generating the message."}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="tel"
          inputMode="numeric"
          value={phoneNumber}
          readOnly
          placeholder="e.g. +919876543210"
          className="w-full min-w-0 sm:w-[200px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
        />
        {hasSent && (
          <button
            type="button"
            disabled={sending || !canSend}
            onClick={() => triggerSend("resend")}
            className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {sending ? "Sending…" : "Resend Message"}
          </button>
        )}
      </div>

      <ConfirmMessageModal
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        previewContent={previewMessage ?? "—"}
        onConfirm={handleConfirmProceed}
        disableConfirm={!canSend}
      />
    </form>
  );
}
